import {
  CheckBox,
  Divider,
  IndexPath,
  Select,
  SelectItem,
  Text,
} from "@ui-kitten/components";
import BaseCard from "components/BaseCard";
import Button from "components/Button";
import Spacer from "components/Spacer";
import { setSkipAddingPlans } from "features/slices/addMembershipFlowSlice";
import {
  setSelectedBillingInterval,
  setSelectedPlanPackage,
  setSignupCharge,
  setStep,
} from "features/slices/buyFlowSlice";
import { BillingInterval, PlanPackageDetails } from "features/types";
import { useAccount } from "hooks/useAccount";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import useIsSSO from "hooks/useIsSSO";
import React, { useEffect, useState } from "react";
import { View } from "react-native";
import { billingIntervalDisplayValue } from "utils/planPackageUtils";
import PlanDescriptionComponent from "../../components/PlanDescriptionComponent";

interface StepOneProps {
  planPackages: PlanPackageDetails[];
}
const StepOne: React.FC<StepOneProps> = ({ planPackages }) => {
  const { selectedBillingInterval, selectedPlanPackage } = useAppSelector(
    (state) => state.buyFlow
  );
  const { skipAddingPlans } = useAppSelector(
    (state) => state.addMembershipFlow
  );
  const { account } = useAccount();
  const accountSubscriptions = account?.subscriptions || [];
  const { authMethod } = useIsSSO();
  const { tenantSubdomain } = useAppSelector((state) => state.tenant);
  const dispatch = useAppDispatch();
  const [billingIntervalIndex, setBillingIntervalIndex] = useState<IndexPath>(
    new IndexPath(0)
  );
  const [availableBillingIntervals, setAvailableBillingIntervals] = useState<
    BillingInterval[]
  >([]);

  const displayValue = selectedPlanPackage?.name || "";

  const billingIntervaldisplayValue = billingIntervalDisplayValue(
    selectedBillingInterval
  );

  const getSelectedIndex = () => {
    const index = planPackages.findIndex(
      (plan) => plan.id === selectedPlanPackage?.id
    );
    return new IndexPath(index);
  };

  // https://github.com/FanRally/fanrally-core/issues/571
  // When adding a new subscription to an existing account, all of the subscriptions of the same plan package must use the same billing interval!
  const getAvailableBillingIntervals = () => {
    const accountHasSelectedPlanPackage = accountSubscriptions.find(
      (sub) =>
        sub.planPackage.id === selectedPlanPackage?.id &&
        sub.status === "ACTIVE"
    );
    if (accountHasSelectedPlanPackage) {
      return [accountHasSelectedPlanPackage.billingInterval];
    }
    return selectedPlanPackage?.billingIntervals || [];
  };

  const initiateBillingInterval = () => {
    const billingIntervals = getAvailableBillingIntervals();
    if (billingIntervals.length === 1) {
      dispatch(setSelectedBillingInterval(billingIntervals[0]));
    }
    setAvailableBillingIntervals(billingIntervals);
  };

  useEffect(() => {
    if (!selectedPlanPackage) return;
    initiateBillingInterval();
  }, [selectedPlanPackage]);

  if (authMethod === "ballpark") {
    return (
      <Text category="h5" style={{ textAlign: "center" }}>
        In order to add additional members to your account, please visit{" "}
        <Text category="h5" status="info">
          {tenantSubdomain as string}.go.fanrally.com
        </Text>
        , and login to your account to view available plans.
      </Text>
    );
  }

  // Common continue button component
  const ContinueButton = () => (
    <Button
      onPress={() =>
        setTimeout(() => {
          dispatch(setStep(2)); // to avoid Stripe elements not mounted bug
        }, 200)
      }
      disabled={!selectedPlanPackage && !skipAddingPlans}
      testID="add-membership-step-one-button"
    >
      Continue
    </Button>
  );

  // Common skip plans checkbox component
  const SkipPlansCheckbox = () => (
    <CheckBox
      checked={skipAddingPlans}
      onChange={(checked) => {
        dispatch(setSkipAddingPlans(checked));
        dispatch(setSignupCharge(null));
      }}
    >
      Skip adding plan
    </CheckBox>
  );

  if (planPackages.length === 0 || !selectedPlanPackage) {
    return (
      <View>
        <Text category="h2" style={{ textAlign: "center" }}>
          No available plan packages
        </Text>
        <Spacer />
        <SkipPlansCheckbox />
        <Spacer margin={20} />
        <ContinueButton />
      </View>
    );
  }
  if (planPackages.length === 0 || !selectedPlanPackage) {
    return (
      <View>
        <Text category="h2" style={{ textAlign: "center" }}>
          loading...
        </Text>
        <Spacer />
        <SkipPlansCheckbox />
        <Spacer margin={20} />
        <ContinueButton />
      </View>
    );
  }

  return (
    <View>
      {skipAddingPlans ? (
        // Only show checkbox and continue button when skipping plans
        <>
          <SkipPlansCheckbox />
          <Spacer margin={20} />
          <ContinueButton />
        </>
      ) : (
        // Show full content when not skipping plans
        <>
          <BaseCard>
            <Select
              value={displayValue}
              selectedIndex={getSelectedIndex()}
              onSelect={(index) => {
                dispatch(
                  setSelectedPlanPackage(planPackages[(index as IndexPath).row])
                );
              }}
            >
              {planPackages.map((planPackage) => (
                <SelectItem key={planPackage.id} title={planPackage.name} />
              ))}
            </Select>
            <Spacer />

            <PlanDescriptionComponent
              description={selectedPlanPackage.description}
            />
          </BaseCard>

          {/* <Spacer /> */}

          {/* for kids club, don't show the add adult button */}
          {/* {signupCharge !== null && (
            <>
              {!selectedPlanPackage.disableAddAdultInBuyFlow && (
                <QuantityModifier type="adult" />
              )}
              {!selectedPlanPackage.disableAddChildInBuyFlow &&
                shouldShowChildQuantityModifier && (
                  <QuantityModifier type="child" />
                )}
            </>
          )} */}

          <Spacer />

          {availableBillingIntervals.length > 1 && (
            <BaseCard title="Choose How You Pay">
              <Divider style={{ marginVertical: 15 }} />
              <Select
                value={billingIntervaldisplayValue}
                selectedIndex={billingIntervalIndex}
                onSelect={(index) => {
                  setBillingIntervalIndex(index as IndexPath);
                  dispatch(
                    setSelectedBillingInterval(
                      availableBillingIntervals[(index as IndexPath).row]
                    )
                  );
                }}
              >
                {selectedPlanPackage?.billingIntervals.map((interval) => (
                  <SelectItem
                    key={interval}
                    title={billingIntervalDisplayValue(interval)}
                  />
                ))}
              </Select>
            </BaseCard>
          )}

          <SkipPlansCheckbox />

          <Spacer margin={20} />

          <ContinueButton />
        </>
      )}
    </View>
  );
};

export default StepOne;
